import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { 
  ClipboardList, 
  Wallet, 
  CreditCard, 
  FileSpreadsheet,
  Search,
  Plus,
  Info,
  Pencil,
  Trash2,
  X,
  RefreshCw,
  Play,
  Pause,
  User,
  Archive,
  ArchiveRestore
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { DatePicker } from '../components/DatePicker';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Checkbox } from '../components/ui/checkbox';
import { cn, formatDate, formatDateTime, isOverdue } from '../lib/utils';
import { Toaster } from '../components/ui/sonner';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CATEGORIES = ['Tâche', 'Salaire', 'Paiement', 'Bilan'];
const RECURRENCE_CATEGORIES = ['Salaire', 'Paiement', 'Bilan'];

// Statuts par défaut pour Tâches et Bilan
const DEFAULT_STATUSES = ['À faire', 'En cours', 'En attente', 'Terminé'];

// Statuts spécifiques pour Salaires et Paiements mensuels
const SALARY_PAYMENT_STATUSES = [
  'À faire', 
  'En cours', 
  'En attente', 
  'Sur la Syno', 
  'En ligne', 
  'En attente de validation', 
  'Client', 
  'Validé'
];

// Tous les statuts possibles (pour le filtre "Toutes catégories")
const ALL_STATUSES = [...new Set([...DEFAULT_STATUSES, ...SALARY_PAYMENT_STATUSES])];

// Helper pour obtenir les statuts selon la catégorie
const getStatusesForCategory = (category) => {
  if (category === 'Salaire' || category === 'Paiement') {
    return SALARY_PAYMENT_STATUSES;
  }
  return DEFAULT_STATUSES;
};

const PRIORITIES = [1, 2, 3, 4, 5];
const FREQUENCIES = ['Mensuelle', 'Annuelle'];
const MONTHS = [
  { value: 1, label: 'Janvier' },
  { value: 2, label: 'Février' },
  { value: 3, label: 'Mars' },
  { value: 4, label: 'Avril' },
  { value: 5, label: 'Mai' },
  { value: 6, label: 'Juin' },
  { value: 7, label: 'Juillet' },
  { value: 8, label: 'Août' },
  { value: 9, label: 'Septembre' },
  { value: 10, label: 'Octobre' },
  { value: 11, label: 'Novembre' },
  { value: 12, label: 'Décembre' }
];

const categoryColors = {
  'Tâche': 'badge-tache',
  'Salaire': 'badge-salaire',
  'Paiement': 'badge-paiement',
  'Bilan': 'badge-bilan'
};

const statusColors = {
  'À faire': 'bg-slate-100 text-slate-600',
  'En cours': 'bg-blue-100 text-blue-700',
  'En attente': 'bg-yellow-100 text-yellow-700',
  'Terminé': 'bg-emerald-100 text-emerald-700',
  'Sur la Syno': 'bg-purple-100 text-purple-700',
  'En ligne': 'bg-sky-100 text-sky-700',
  'En attente de validation': 'bg-orange-100 text-orange-700',
  'Client': 'bg-indigo-100 text-indigo-700',
  'Validé': 'bg-emerald-100 text-emerald-700'
};

const PriorityIndicator = ({ priority }) => {
  return (
    <div className="priority-indicator" style={{ color: priority >= 4 ? '#ef4444' : priority >= 3 ? '#f59e0b' : '#94a3b8' }}>
      {[1, 2, 3, 4, 5].map((p) => (
        <div 
          key={p} 
          className={`priority-dot ${p <= priority ? 'active' : 'inactive'}`}
        />
      ))}
    </div>
  );
};

const StatWidget = ({ title, icon: Icon, total, overdue, color }) => (
  <div className="widget-card" data-testid={`widget-${title.toLowerCase()}`}>
    <div className="flex items-center justify-between mb-3">
      <span className="text-sm font-medium text-slate-500">{title}</span>
      <div className={`p-2 rounded-lg ${color}`}>
        <Icon size={18} />
      </div>
    </div>
    <div className="flex items-baseline gap-2">
      <span className="text-2xl font-bold text-slate-900 tabular-nums">{total}</span>
      {overdue > 0 && (
        <span className="text-sm font-medium text-red-500">
          ({overdue} en retard)
        </span>
      )}
    </div>
  </div>
);

// ============== TABLEAU DE BORD COMPONENT ==============
const TableauDeBord = ({ tasks, clients, users, stats, loading, onRefresh, currentUserId }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('Toutes');
  const [statusFilter, setStatusFilter] = useState('Tous');
  const [priorityFilter, setPriorityFilter] = useState('Toutes');
  const [assignedFilter, setAssignedFilter] = useState('Tous');
  const [clientFilter, setClientFilter] = useState('Tous');
  const [myTasksOnly, setMyTasksOnly] = useState(false);
  
  const [showForm, setShowForm] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [editMode, setEditMode] = useState(false);
  
  const [formData, setFormData] = useState({
    category: 'Tâche',
    title: '',
    client_id: '',
    assigned_to: [],  // Changed to array for multi-assignment
    status: 'À faire',
    priority: 3,
    deadline: '',
    notes: ''
  });

  // Génère le titre et deadline automatiques pour Salaires/Paiements
  const handleCategoryChange = (category) => {
    const now = new Date();
    const currentMonth = (now.getMonth() + 1).toString().padStart(2, '0');
    const currentYear = now.getFullYear();
    
    let newTitle = formData.title;
    let newDeadline = formData.deadline;
    let newStatus = formData.status;
    
    // Si c'est une nouvelle création (pas en mode édition), pré-remplir le titre et la deadline
    if (!editMode) {
      if (category === 'Salaire') {
        newTitle = `Salaire mensuel ${currentMonth}.${currentYear}`;
        // Deadline = 5 du mois suivant
        const nextMonth = new Date(currentYear, now.getMonth() + 1, 5);
        newDeadline = nextMonth.toISOString();
        newStatus = 'À faire';
      } else if (category === 'Paiement') {
        newTitle = `Paiement mensuel ${currentMonth}.${currentYear}`;
        // Deadline = 5 du mois suivant
        const nextMonth = new Date(currentYear, now.getMonth() + 1, 5);
        newDeadline = nextMonth.toISOString();
        newStatus = 'À faire';
      } else if (category === 'Tâche' || category === 'Bilan') {
        // Réinitialiser le titre si ce n'était pas une tâche/bilan avant
        if (formData.category === 'Salaire' || formData.category === 'Paiement') {
          newTitle = '';
          newDeadline = '';
        }
        newStatus = DEFAULT_STATUSES.includes(formData.status) ? formData.status : 'À faire';
      }
    }
    
    // Vérifier si le statut actuel est valide pour la nouvelle catégorie
    const validStatuses = getStatusesForCategory(category);
    if (!validStatuses.includes(newStatus)) {
      newStatus = 'À faire';
    }
    
    setFormData({
      ...formData,
      category,
      title: newTitle,
      deadline: newDeadline,
      status: newStatus
    });
  };

  const filteredTasks = useMemo(() => {
    return tasks.filter(task => {
      // Filter "Mes tâches" - now checks array
      const assignedArray = Array.isArray(task.assigned_to) ? task.assigned_to : [task.assigned_to];
      if (myTasksOnly && !assignedArray.includes(currentUserId)) return false;
      
      if (searchQuery) {
        const client = clients.find(c => c.id === task.client_id);
        const assignedUsers = users.filter(u => assignedArray.includes(u.id));
        const searchLower = searchQuery.toLowerCase();
        const matchesSearch = 
          task.title.toLowerCase().includes(searchLower) ||
          client?.company_name?.toLowerCase().includes(searchLower) ||
          assignedUsers.some(u => `${u?.first_name} ${u?.last_name}`.toLowerCase().includes(searchLower));
        if (!matchesSearch) return false;
      }
      if (categoryFilter !== 'Toutes' && task.category !== categoryFilter) return false;
      if (statusFilter !== 'Tous' && task.status !== statusFilter) return false;
      if (priorityFilter !== 'Toutes' && task.priority !== parseInt(priorityFilter)) return false;
      if (assignedFilter !== 'Tous' && !assignedArray.includes(assignedFilter)) return false;
      if (clientFilter !== 'Tous' && task.client_id !== clientFilter) return false;
      return true;
    });
  }, [tasks, searchQuery, categoryFilter, statusFilter, priorityFilter, assignedFilter, clientFilter, clients, users, myTasksOnly, currentUserId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMode && selectedTask) {
        await axios.put(`${API_URL}/tasks/${selectedTask.id}`, formData);
        toast.success('Tâche mise à jour');
      } else {
        await axios.post(`${API_URL}/tasks`, formData);
        toast.success('Tâche créée');
      }
      setShowForm(false);
      resetForm();
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async () => {
    if (!selectedTask) return;
    try {
      await axios.delete(`${API_URL}/tasks/${selectedTask.id}`);
      toast.success('Tâche supprimée');
      setShowDeleteConfirm(false);
      setShowDetail(false);
      setSelectedTask(null);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleArchive = async () => {
    if (!selectedTask) return;
    try {
      await axios.put(`${API_URL}/tasks/${selectedTask.id}/archive`);
      toast.success('Élément archivé');
      setShowDetail(false);
      setSelectedTask(null);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'archivage');
    }
  };

  const handleArchiveAllCompleted = async () => {
    try {
      const response = await axios.post(`${API_URL}/tasks/archive-completed`);
      toast.success(response.data.message);
      onRefresh();
    } catch (error) {
      toast.error('Erreur lors de l\'archivage');
    }
  };

  const canArchive = (task) => {
    const completedStatuses = ['Terminé', 'Validé'];
    return completedStatuses.includes(task?.status);
  };

  const resetForm = () => {
    setFormData({
      category: 'Tâche',
      title: '',
      client_id: '',
      assigned_to: [],
      status: 'À faire',
      priority: 3,
      deadline: '',
      notes: ''
    });
    setEditMode(false);
    setSelectedTask(null);
  };

  const openDetail = (task) => {
    setSelectedTask(task);
    setShowDetail(true);
  };

  const openEdit = () => {
    if (selectedTask) {
      // Ensure assigned_to is an array
      const assignedTo = Array.isArray(selectedTask.assigned_to) 
        ? selectedTask.assigned_to 
        : selectedTask.assigned_to ? [selectedTask.assigned_to] : [];
      
      setFormData({
        category: selectedTask.category,
        title: selectedTask.title,
        client_id: selectedTask.client_id,
        assigned_to: assignedTo,
        status: selectedTask.status,
        priority: selectedTask.priority,
        deadline: selectedTask.deadline,
        notes: selectedTask.notes || ''
      });
      setEditMode(true);
      setShowDetail(false);
      setShowForm(true);
    }
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client?.company_name || '-';
  };

  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user ? `${user.first_name} ${user.last_name}` : '-';
  };

  // Get names for multiple assigned users
  const getAssignedNames = (assignedTo) => {
    if (!assignedTo) return '-';
    const ids = Array.isArray(assignedTo) ? assignedTo : [assignedTo];
    if (ids.length === 0) return '-';
    const names = ids.map(id => getUserName(id)).filter(n => n !== '-');
    return names.length > 0 ? names.join(', ') : '-';
  };

  // Toggle user in assigned_to array
  const toggleAssignedUser = (userId) => {
    const currentAssigned = Array.isArray(formData.assigned_to) ? formData.assigned_to : [];
    if (currentAssigned.includes(userId)) {
      setFormData({...formData, assigned_to: currentAssigned.filter(id => id !== userId)});
    } else {
      setFormData({...formData, assigned_to: [...currentAssigned, userId]});
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-end">
        <Button 
          onClick={() => { resetForm(); setShowForm(true); }}
          className="rounded-lg"
          data-testid="new-task-button"
        >
          <Plus size={18} className="mr-2" />
          Nouveau
        </Button>
      </div>

      {/* Widgets */}
      <div className="widgets-grid">
        <StatWidget 
          title="Tâches" 
          icon={ClipboardList}
          total={stats?.taches?.total || 0}
          overdue={stats?.taches?.overdue || 0}
          color="bg-blue-50 text-blue-600"
        />
        <StatWidget 
          title="Salaires" 
          icon={Wallet}
          total={stats?.salaires?.total || 0}
          overdue={stats?.salaires?.overdue || 0}
          color="bg-purple-50 text-purple-600"
        />
        <StatWidget 
          title="Paiements" 
          icon={CreditCard}
          total={stats?.paiements?.total || 0}
          overdue={stats?.paiements?.overdue || 0}
          color="bg-amber-50 text-amber-600"
        />
        <StatWidget 
          title="Bilan" 
          icon={FileSpreadsheet}
          total={stats?.bilan?.total || 0}
          overdue={stats?.bilan?.overdue || 0}
          color="bg-emerald-50 text-emerald-600"
        />
      </div>

      {/* Search bar */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
        <Input
          type="text"
          placeholder="Rechercher par client, tâche, personne assignée..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-11 h-11 border-slate-200"
          data-testid="search-input"
        />
      </div>

      {/* Filters */}
      <div className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="filter-pills">
            {['Toutes', ...CATEGORIES].map((cat) => (
              <button
                key={cat}
                onClick={() => setCategoryFilter(cat)}
                className={`filter-pill ${categoryFilter === cat ? 'active' : ''}`}
                data-testid={`filter-category-${cat.toLowerCase()}`}
              >
                {cat}
              </button>
            ))}
          </div>
          
          {/* Quick filter: My Tasks */}
          <button
            onClick={() => setMyTasksOnly(!myTasksOnly)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              myTasksOnly 
                ? 'bg-primary text-white' 
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
            data-testid="filter-my-tasks"
          >
            {myTasksOnly ? '✓ Mes tâches' : 'Mes tâches'}
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="filter-status">
              <SelectValue placeholder="Statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Tous">Tous les statuts</SelectItem>
              {(categoryFilter === 'Toutes' 
                ? ALL_STATUSES 
                : getStatusesForCategory(categoryFilter)
              ).map(s => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={priorityFilter} onValueChange={setPriorityFilter}>
            <SelectTrigger data-testid="filter-priority">
              <SelectValue placeholder="Priorité" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Toutes">Toutes priorités</SelectItem>
              {PRIORITIES.map(p => (
                <SelectItem key={p} value={p.toString()}>Priorité {p}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={assignedFilter} onValueChange={setAssignedFilter}>
            <SelectTrigger data-testid="filter-assigned">
              <SelectValue placeholder="Assigné à" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Tous">Tous</SelectItem>
              {users.map(u => (
                <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={clientFilter} onValueChange={setClientFilter}>
            <SelectTrigger data-testid="filter-client">
              <SelectValue placeholder="Client" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Tous">Tous les clients</SelectItem>
              {clients.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Archive completed button */}
      <div className="flex justify-end">
        <Button 
          variant="outline" 
          onClick={handleArchiveAllCompleted}
          className="rounded-lg"
          data-testid="archive-all-completed-btn"
        >
          <Archive size={16} className="mr-2" />
          Archiver les éléments terminés
        </Button>
      </div>

      {/* Tasks list */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        {filteredTasks.length === 0 ? (
          <div className="empty-state">
            <ClipboardList size={48} className="mx-auto mb-4 text-slate-300" />
            <p className="text-lg font-medium">Aucune tâche trouvée</p>
            <p className="text-sm">Ajustez vos filtres ou créez une nouvelle tâche</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table responsive-table" data-testid="tasks-table">
              <thead>
                <tr>
                  <th>Catégorie</th>
                  <th>Titre</th>
                  <th>Client</th>
                  <th>Assigné à</th>
                  <th>Statut</th>
                  <th>Priorité</th>
                  <th>Deadline</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredTasks.map((task) => (
                  <tr key={task.id} data-testid={`task-row-${task.id}`}>
                    <td data-label="Catégorie">
                      <Badge className={categoryColors[task.category]}>
                        {task.category}
                      </Badge>
                    </td>
                    <td data-label="Titre" className="font-medium">{task.title}</td>
                    <td data-label="Client">{getClientName(task.client_id)}</td>
                    <td data-label="Assigné à">{getAssignedNames(task.assigned_to)}</td>
                    <td data-label="Statut">
                      <Badge className={statusColors[task.status]}>
                        {task.status}
                      </Badge>
                    </td>
                    <td data-label="Priorité">
                      <PriorityIndicator priority={task.priority} />
                    </td>
                    <td data-label="Deadline" className={isOverdue(task.deadline) && task.status !== 'Terminé' ? 'text-red-500 font-medium' : ''}>
                      {formatDate(task.deadline)}
                    </td>
                    <td>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openDetail(task)}
                        className="rounded-lg"
                        data-testid={`task-info-${task.id}`}
                      >
                        <Info size={16} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg" data-testid="task-form-dialog">
          <DialogHeader>
            <DialogTitle>{editMode ? 'Modifier la tâche' : 'Nouvelle tâche'}</DialogTitle>
            <DialogDescription>
              {editMode ? 'Modifiez les informations de la tâche' : 'Remplissez les informations pour créer une nouvelle tâche'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Catégorie</Label>
                <Select value={formData.category} onValueChange={handleCategoryChange}>
                  <SelectTrigger data-testid="form-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Priorité</Label>
                <Select value={formData.priority.toString()} onValueChange={(v) => setFormData({...formData, priority: parseInt(v)})}>
                  <SelectTrigger data-testid="form-priority">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map(p => (
                      <SelectItem key={p} value={p.toString()}>{p} {p === 1 ? '(Basse)' : p === 5 ? '(Urgente)' : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Titre</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                required
                data-testid="form-title"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Client</Label>
                <Select value={formData.client_id} onValueChange={(v) => setFormData({...formData, client_id: v})}>
                  <SelectTrigger data-testid="form-client">
                    <SelectValue placeholder="Sélectionner..." />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Assigné à (sélection multiple)</Label>
                <div className="border rounded-lg p-3 max-h-40 overflow-y-auto space-y-2" data-testid="form-assigned-multi">
                  {users.map(u => {
                    const isSelected = Array.isArray(formData.assigned_to) && formData.assigned_to.includes(u.id);
                    return (
                      <div key={u.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`user-${u.id}`}
                          checked={isSelected}
                          onCheckedChange={() => toggleAssignedUser(u.id)}
                        />
                        <label
                          htmlFor={`user-${u.id}`}
                          className="text-sm cursor-pointer flex-1"
                        >
                          {u.first_name} {u.last_name}
                        </label>
                      </div>
                    );
                  })}
                </div>
                {formData.assigned_to.length > 0 && (
                  <p className="text-xs text-slate-500">
                    {formData.assigned_to.length} personne(s) sélectionnée(s)
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Statut</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                  <SelectTrigger data-testid="form-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {getStatusesForCategory(formData.category).map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Deadline</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn("w-full justify-start text-left font-normal", !formData.deadline && "text-muted-foreground")}
                      data-testid="form-deadline"
                    >
                      {formData.deadline ? format(new Date(formData.deadline), 'dd/MM/yyyy', { locale: fr }) : 'Sélectionner...'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={formData.deadline ? new Date(formData.deadline) : undefined}
                      onSelect={(date) => setFormData({...formData, deadline: date?.toISOString() || ''})}
                      locale={fr}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                rows={3}
                data-testid="form-notes"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="rounded-lg">
                Annuler
              </Button>
              <Button type="submit" className="rounded-lg" data-testid="form-submit">
                {editMode ? 'Enregistrer' : 'Créer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-lg" data-testid="task-detail-dialog">
          <DialogHeader>
            <DialogTitle>Détails de la tâche</DialogTitle>
            <DialogDescription>
              Informations complètes et historique des modifications
            </DialogDescription>
          </DialogHeader>
          {selectedTask && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-500 text-xs">Catégorie</Label>
                  <p><Badge className={categoryColors[selectedTask.category]}>{selectedTask.category}</Badge></p>
                </div>
                <div>
                  <Label className="text-slate-500 text-xs">Priorité</Label>
                  <PriorityIndicator priority={selectedTask.priority} />
                </div>
              </div>
              
              <div>
                <Label className="text-slate-500 text-xs">Titre</Label>
                <p className="font-medium">{selectedTask.title}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-500 text-xs">Client</Label>
                  <p>{getClientName(selectedTask.client_id)}</p>
                </div>
                <div>
                  <Label className="text-slate-500 text-xs">Assigné à</Label>
                  <p>{getAssignedNames(selectedTask.assigned_to)}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-500 text-xs">Statut</Label>
                  <p><Badge className={statusColors[selectedTask.status]}>{selectedTask.status}</Badge></p>
                </div>
                <div>
                  <Label className="text-slate-500 text-xs">Deadline</Label>
                  <p className={isOverdue(selectedTask.deadline) && selectedTask.status !== 'Terminé' ? 'text-red-500' : ''}>
                    {formatDate(selectedTask.deadline)}
                  </p>
                </div>
              </div>
              
              {selectedTask.notes && (
                <div>
                  <Label className="text-slate-500 text-xs">Notes</Label>
                  <p className="text-sm">{selectedTask.notes}</p>
                </div>
              )}
              
              <div>
                <Label className="text-slate-500 text-xs">Date d'ajout</Label>
                <p className="text-sm">{formatDateTime(selectedTask.created_at)}</p>
              </div>
              
              {selectedTask.history && selectedTask.history.length > 0 && (
                <div>
                  <Label className="text-slate-500 text-xs mb-2 block">Historique des modifications</Label>
                  <div className="max-h-40 overflow-y-auto space-y-2 bg-slate-50 rounded-lg p-3">
                    {selectedTask.history.map((entry, idx) => (
                      <div key={idx} className="text-xs border-b border-slate-100 pb-2 last:border-0">
                        <span className="text-slate-500">{formatDateTime(entry.timestamp)}</span>
                        <span className="mx-1">—</span>
                        <span className="font-medium">{entry.user_name}</span>
                        <span className="mx-1">a modifié</span>
                        <span className="font-medium">{entry.field}</span>
                        <span className="mx-1">:</span>
                        <span className="text-slate-500">{entry.old_value || '(vide)'}</span>
                        <span className="mx-1">→</span>
                        <span>{entry.new_value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <DialogFooter className="gap-2 flex-wrap">
                <Button variant="destructive" onClick={() => setShowDeleteConfirm(true)} className="rounded-lg" data-testid="delete-task-button">
                  <Trash2 size={16} className="mr-2" />
                  Supprimer
                </Button>
                {canArchive(selectedTask) && (
                  <Button 
                    variant="outline" 
                    onClick={handleArchive} 
                    className="rounded-lg"
                    data-testid="archive-task-button"
                  >
                    <Archive size={16} className="mr-2" />
                    Archiver
                  </Button>
                )}
                <Button onClick={openEdit} className="rounded-lg" data-testid="edit-task-button">
                  <Pencil size={16} className="mr-2" />
                  Modifier
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer cette tâche ? Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-lg">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="rounded-lg bg-destructive" data-testid="confirm-delete-button">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ============== RECURRENCES COMPONENT ==============
const Recurrences = ({ clients, users, onRefresh }) => {
  const [recurrences, setRecurrences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedRecurrence, setSelectedRecurrence] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [generating, setGenerating] = useState(false);
  
  const [formData, setFormData] = useState({
    category: 'Salaire',
    title_template: '',
    client_id: '',
    assigned_to: '',
    priority: 3,
    frequency: 'Mensuelle',
    deadline_days: 15,
    annual_generation_month: 1,
    notes: '',
    is_active: true
  });

  useEffect(() => {
    fetchRecurrences();
  }, []);

  const fetchRecurrences = async () => {
    try {
      const response = await axios.get(`${API_URL}/recurrences`);
      setRecurrences(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des récurrences');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMode && selectedRecurrence) {
        await axios.put(`${API_URL}/recurrences/${selectedRecurrence.id}`, formData);
        toast.success('Récurrence mise à jour');
      } else {
        await axios.post(`${API_URL}/recurrences`, formData);
        toast.success('Récurrence créée');
      }
      setShowForm(false);
      resetForm();
      fetchRecurrences();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    }
  };

  const handleToggleActive = async (recurrence) => {
    try {
      await axios.put(`${API_URL}/recurrences/${recurrence.id}`, { is_active: !recurrence.is_active });
      toast.success(recurrence.is_active ? 'Récurrence désactivée' : 'Récurrence activée');
      fetchRecurrences();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async () => {
    if (!selectedRecurrence) return;
    try {
      await axios.delete(`${API_URL}/recurrences/${selectedRecurrence.id}`);
      toast.success('Récurrence supprimée');
      setShowDeleteConfirm(false);
      setSelectedRecurrence(null);
      fetchRecurrences();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API_URL}/recurrences/generate`);
      toast.success(response.data.message);
      onRefresh();
    } catch (error) {
      toast.error('Erreur lors de la génération');
    } finally {
      setGenerating(false);
    }
  };

  const resetForm = () => {
    setFormData({
      category: 'Salaire',
      title_template: '',
      client_id: '',
      assigned_to: '',
      priority: 3,
      frequency: 'Mensuelle',
      deadline_days: 15,
      annual_generation_month: 1,
      notes: '',
      is_active: true
    });
    setEditMode(false);
    setSelectedRecurrence(null);
  };

  const openEdit = (recurrence) => {
    setSelectedRecurrence(recurrence);
    setFormData({
      category: recurrence.category,
      title_template: recurrence.title_template,
      client_id: recurrence.client_id,
      assigned_to: recurrence.assigned_to,
      priority: recurrence.priority,
      frequency: recurrence.frequency,
      deadline_days: recurrence.deadline_days,
      annual_generation_month: recurrence.annual_generation_month,
      notes: recurrence.notes || '',
      is_active: recurrence.is_active
    });
    setEditMode(true);
    setShowForm(true);
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client?.company_name || '-';
  };

  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user ? `${user.first_name} ${user.last_name}` : '-';
  };

  const getMonthName = (month) => {
    return MONTHS.find(m => m.value === month)?.label || '-';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">
          Configurez des tâches qui se génèrent automatiquement de façon récurrente
        </p>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={handleGenerate}
            disabled={generating}
            className="rounded-lg"
            data-testid="generate-tasks-button"
          >
            <RefreshCw size={18} className={`mr-2 ${generating ? 'animate-spin' : ''}`} />
            Générer maintenant
          </Button>
          <Button 
            onClick={() => { resetForm(); setShowForm(true); }}
            className="rounded-lg"
            data-testid="new-recurrence-button"
          >
            <Plus size={18} className="mr-2" />
            Nouvelle récurrence
          </Button>
        </div>
      </div>

      {/* Recurrences list */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        {recurrences.length === 0 ? (
          <div className="empty-state">
            <RefreshCw size={48} className="mx-auto mb-4 text-slate-300" />
            <p className="text-lg font-medium">Aucune récurrence configurée</p>
            <p className="text-sm">Créez votre première récurrence pour automatiser les tâches</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table responsive-table" data-testid="recurrences-table">
              <thead>
                <tr>
                  <th>Catégorie</th>
                  <th>Titre modèle</th>
                  <th>Client</th>
                  <th>Assigné à</th>
                  <th>Priorité</th>
                  <th>Fréquence</th>
                  <th>Deadline</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {recurrences.map((rec) => (
                  <tr key={rec.id} data-testid={`recurrence-row-${rec.id}`}>
                    <td data-label="Catégorie">
                      <Badge className={categoryColors[rec.category]}>
                        {rec.category}
                      </Badge>
                    </td>
                    <td data-label="Titre" className="font-medium">{rec.title_template}</td>
                    <td data-label="Client">{getClientName(rec.client_id)}</td>
                    <td data-label="Assigné à">{getUserName(rec.assigned_to)}</td>
                    <td data-label="Priorité">
                      <PriorityIndicator priority={rec.priority} />
                    </td>
                    <td data-label="Fréquence">
                      <span className="text-sm">
                        {rec.frequency}
                        {rec.frequency === 'Annuelle' && (
                          <span className="text-slate-500 ml-1">({getMonthName(rec.annual_generation_month)})</span>
                        )}
                      </span>
                    </td>
                    <td data-label="Deadline">
                      <span className="text-sm">+{rec.deadline_days} jours</span>
                    </td>
                    <td data-label="Statut">
                      <Badge className={rec.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}>
                        {rec.is_active ? 'Actif' : 'Inactif'}
                      </Badge>
                    </td>
                    <td data-label="Actions">
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEdit(rec)}
                          className="rounded-lg"
                          data-testid={`edit-recurrence-${rec.id}`}
                        >
                          <Pencil size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleActive(rec)}
                          className={`rounded-lg ${rec.is_active ? 'text-amber-500 hover:text-amber-600 hover:bg-amber-50' : 'text-emerald-500 hover:text-emerald-600 hover:bg-emerald-50'}`}
                          data-testid={`toggle-recurrence-${rec.id}`}
                        >
                          {rec.is_active ? <Pause size={16} /> : <Play size={16} />}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { setSelectedRecurrence(rec); setShowDeleteConfirm(true); }}
                          className="rounded-lg text-red-500 hover:text-red-600 hover:bg-red-50"
                          data-testid={`delete-recurrence-${rec.id}`}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg" data-testid="recurrence-form-dialog">
          <DialogHeader>
            <DialogTitle>{editMode ? 'Modifier la récurrence' : 'Nouvelle récurrence'}</DialogTitle>
            <DialogDescription>
              Configurez une tâche récurrente qui sera générée automatiquement
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Catégorie</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                  <SelectTrigger data-testid="rec-form-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RECURRENCE_CATEGORIES.map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Fréquence</Label>
                <Select value={formData.frequency} onValueChange={(v) => setFormData({...formData, frequency: v})}>
                  <SelectTrigger data-testid="rec-form-frequency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FREQUENCIES.map(f => (
                      <SelectItem key={f} value={f}>{f}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Titre modèle</Label>
              <Input
                value={formData.title_template}
                onChange={(e) => setFormData({...formData, title_template: e.target.value})}
                placeholder="Ex: Salaires {mois} {année}"
                required
                data-testid="rec-form-title"
              />
              <p className="text-xs text-slate-500">
                Utilisez {'{mois}'} et {'{année}'} comme variables
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Client</Label>
                <Select value={formData.client_id} onValueChange={(v) => setFormData({...formData, client_id: v})}>
                  <SelectTrigger data-testid="rec-form-client">
                    <SelectValue placeholder="Sélectionner..." />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Assigné à</Label>
                <Select value={formData.assigned_to} onValueChange={(v) => setFormData({...formData, assigned_to: v})}>
                  <SelectTrigger data-testid="rec-form-assigned">
                    <SelectValue placeholder="Sélectionner..." />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Priorité par défaut</Label>
                <Select value={formData.priority.toString()} onValueChange={(v) => setFormData({...formData, priority: parseInt(v)})}>
                  <SelectTrigger data-testid="rec-form-priority">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map(p => (
                      <SelectItem key={p} value={p.toString()}>{p} {p === 1 ? '(Basse)' : p === 5 ? '(Urgente)' : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Deadline relative (jours)</Label>
                <Input
                  type="number"
                  value={formData.deadline_days}
                  onChange={(e) => setFormData({...formData, deadline_days: parseInt(e.target.value) || 15})}
                  min={1}
                  max={365}
                  data-testid="rec-form-deadline"
                />
              </div>
            </div>

            {formData.frequency === 'Annuelle' && (
              <div className="space-y-2">
                <Label>Mois de génération annuelle</Label>
                <Select value={formData.annual_generation_month.toString()} onValueChange={(v) => setFormData({...formData, annual_generation_month: parseInt(v)})}>
                  <SelectTrigger data-testid="rec-form-annual-month">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTHS.map(m => (
                      <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label>Notes par défaut</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                rows={2}
                data-testid="rec-form-notes"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="rounded-lg">
                Annuler
              </Button>
              <Button type="submit" className="rounded-lg" data-testid="rec-form-submit">
                {editMode ? 'Enregistrer' : 'Créer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent data-testid="delete-recurrence-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer cette récurrence ? Les tâches déjà générées ne seront pas affectées.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-lg">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="rounded-lg bg-destructive" data-testid="confirm-delete-recurrence">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ============== ARCHIVES COMPONENT ==============
const Archives = ({ clients, users, onRefresh }) => {
  const [archivedTasks, setArchivedTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('Toutes');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    fetchArchived();
  }, []);

  const fetchArchived = async () => {
    try {
      const response = await axios.get(`${API_URL}/tasks/archived`);
      setArchivedTasks(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des archives');
    } finally {
      setLoading(false);
    }
  };

  const handleUnarchive = async (taskId) => {
    try {
      await axios.put(`${API_URL}/tasks/${taskId}/unarchive`);
      toast.success('Élément désarchivé');
      fetchArchived();
      onRefresh();
    } catch (error) {
      toast.error('Erreur lors du désarchivage');
    }
  };

  const filteredTasks = archivedTasks.filter(task => {
    if (searchQuery) {
      const client = clients.find(c => c.id === task.client_id);
      const searchLower = searchQuery.toLowerCase();
      if (!task.title.toLowerCase().includes(searchLower) &&
          !client?.company_name?.toLowerCase().includes(searchLower)) {
        return false;
      }
    }
    if (categoryFilter !== 'Toutes' && task.category !== categoryFilter) return false;
    
    if (dateFrom && task.archived_at) {
      const archivedDate = new Date(task.archived_at);
      const fromDate = new Date(dateFrom);
      if (archivedDate < fromDate) return false;
    }
    if (dateTo && task.archived_at) {
      const archivedDate = new Date(task.archived_at);
      const toDate = new Date(dateTo);
      toDate.setHours(23, 59, 59);
      if (archivedDate > toDate) return false;
    }
    
    return true;
  });

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client?.company_name || '-';
  };

  if (loading) {
    return <div className="py-8 text-center text-slate-500">Chargement...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-end">
        <div className="flex-1 min-w-64">
          <Input
            type="text"
            placeholder="Rechercher..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="rounded-lg"
          />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Toutes">Toutes catégories</SelectItem>
            {CATEGORIES.map(c => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          <div className="flex flex-col">
            <label className="text-xs text-slate-500 mb-1">Archivé du</label>
            <DatePicker
              value={dateFrom}
              onChange={(iso) => setDateFrom(iso)}
              className="w-44"
              testId="suivi-archives-date-from"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-xs text-slate-500 mb-1">Au</label>
            <DatePicker
              value={dateTo}
              onChange={(iso) => setDateTo(iso)}
              className="w-44"
              testId="suivi-archives-date-to"
            />
          </div>
          {(dateFrom || dateTo) && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => { setDateFrom(''); setDateTo(''); }}
              className="mt-4"
            >
              <X size={14} className="mr-1" />
              Réinitialiser
            </Button>
          )}
        </div>
      </div>

      {/* Archived tasks list */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        {filteredTasks.length === 0 ? (
          <div className="empty-state">
            <Archive size={48} className="mx-auto mb-4 text-slate-300" />
            <p className="text-lg font-medium">Aucun élément archivé</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Catégorie</th>
                  <th>Titre</th>
                  <th>Client</th>
                  <th>Statut</th>
                  <th>Archivé le</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredTasks.map((task) => (
                  <tr key={task.id}>
                    <td>
                      <Badge className={categoryColors[task.category]}>
                        {task.category}
                      </Badge>
                    </td>
                    <td className="font-medium">{task.title}</td>
                    <td>{getClientName(task.client_id)}</td>
                    <td>
                      <Badge className={statusColors[task.status]}>
                        {task.status}
                      </Badge>
                    </td>
                    <td>{task.archived_at ? formatDate(task.archived_at) : '-'}</td>
                    <td>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleUnarchive(task.id)}
                        className="text-primary"
                      >
                        <ArchiveRestore size={16} className="mr-1" />
                        Désarchiver
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ============== MAIN SUIVI COMPONENT ==============
const Suivi = () => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [tasksRes, clientsRes, usersRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/tasks`),
        axios.get(`${API_URL}/clients`),
        axios.get(`${API_URL}/users`),
        axios.get(`${API_URL}/tasks/stats/summary`)
      ]);
      setTasks(tasksRes.data);
      setClients(clientsRes.data);
      setUsers(usersRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="suivi-page">
      <Toaster position="top-right" />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900">Suivi</h1>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">
            <ClipboardList size={16} className="mr-2" />
            Tableau de bord
          </TabsTrigger>
          <TabsTrigger value="recurrences" data-testid="tab-recurrences">
            <RefreshCw size={16} className="mr-2" />
            Récurrences
          </TabsTrigger>
          <TabsTrigger value="archives" data-testid="tab-archives">
            <Archive size={16} className="mr-2" />
            Archives
          </TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard">
          <TableauDeBord 
            tasks={tasks}
            clients={clients}
            users={users}
            stats={stats}
            loading={loading}
            onRefresh={fetchData}
            currentUserId={user?.id}
          />
        </TabsContent>

        <TabsContent value="recurrences">
          <Recurrences 
            clients={clients}
            users={users}
            onRefresh={fetchData}
          />
        </TabsContent>

        <TabsContent value="archives">
          <Archives 
            clients={clients}
            users={users}
            onRefresh={fetchData}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Suivi;
